import collapseBar from "../lottie/collapseBar";
import Lottie from "lottie-react";
import React, { useEffect, useMemo, useRef } from "react";
// Custom color definitions
const customColors = {
    gray: {
        200: "#D3D5D9",
        800: "#2A3241",
    },
};
// Convert hex to RGB array [r, g, b, a]
const hexToRgba = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    return [r, g, b, 1];
};
const colors = {
    light: hexToRgba(customColors.gray[200]),
    dark: hexToRgba(customColors.gray[800]),
};
// Helper to check if array is a color array
const isColorArray = (arr) => {
    return (Array.isArray(arr) &&
        arr.length === 4 &&
        arr.every((n) => typeof n === "number"));
};
// Replace colors in Lottie animation
const replaceColors = (obj, newColor) => {
    if (Array.isArray(obj)) {
        return obj.map((item) => replaceColors(item, newColor));
    }
    else if (obj !== null && typeof obj === "object") {
        for (const key in obj) {
            if (isColorArray(obj[key])) {
                obj[key] = newColor;
            }
            else {
                obj[key] = replaceColors(obj[key], newColor);
            }
        }
    }
    return obj;
};
// Constant to store frame numbers
const FRAMES = {
    INITIAL: 0,
    IDLE: 13,
    LEFT_START: 1,
    RIGHT_END: 25,
};
/**
 * An animated chevron affordance for collapsing or expanding a side panel, playing
 * a Lottie animation on hover that points in the `direction` it controls. Use it as
 * the toggle handle for a collapsible sidebar or rail; it renders only the icon, so
 * wrap it in a tooltip or an accessible control if it needs a label.
 * @summary Panel-collapse chevron handle.
 */
const CollapseButton = ({ direction, variant = "light", }) => {
    const lottieRef = useRef(null);
    const animationData = useMemo(() => replaceColors(JSON.parse(JSON.stringify(collapseBar)), colors[variant]), [variant]);
    // Function to handle hover event
    const handleMouseEnter = () => {
        var _a;
        if ((_a = lottieRef.current) === null || _a === void 0 ? void 0 : _a.animationItem) {
            if (direction === "left") {
                lottieRef.current.animationItem.playSegments([FRAMES.IDLE, FRAMES.LEFT_START], true);
            }
            else if (direction === "right") {
                lottieRef.current.animationItem.playSegments([FRAMES.IDLE, FRAMES.RIGHT_END], true);
            }
        }
    };
    // Function to reset animation to idle state
    const handleMouseLeave = () => {
        var _a;
        if ((_a = lottieRef.current) === null || _a === void 0 ? void 0 : _a.animationItem) {
            if (direction === "left") {
                lottieRef.current.animationItem.playSegments([FRAMES.LEFT_START, FRAMES.IDLE], true);
            }
            else if (direction === "right") {
                lottieRef.current.animationItem.playSegments([FRAMES.RIGHT_END, FRAMES.IDLE], true);
            }
        }
    };
    useEffect(() => {
        // Ensure the animation starts from the initial state
        const initializeAnimation = () => {
            var _a;
            if ((_a = lottieRef.current) === null || _a === void 0 ? void 0 : _a.animationItem) {
                lottieRef.current.animationItem.goToAndStop(FRAMES.INITIAL, true);
                // Set to idle state after initial load
                setTimeout(() => {
                    var _a, _b;
                    (_b = (_a = lottieRef.current) === null || _a === void 0 ? void 0 : _a.animationItem) === null || _b === void 0 ? void 0 : _b.goToAndStop(FRAMES.IDLE, true);
                }, 100);
            }
        };
        // Adding a small delay to ensure the animation is ready
        const timeoutId = setTimeout(initializeAnimation, 100);
        return () => clearTimeout(timeoutId);
    }, []);
    return (React.createElement("div", { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, style: { width: `24px`, height: `30px` }, className: "cursor-pointer" },
        React.createElement(Lottie, { lottieRef: lottieRef, animationData: animationData, loop: false, autoplay: false })));
};
export { CollapseButton };
//# sourceMappingURL=CollapseButton.js.map